import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { Observable, from, of } from "rxjs";
import { UserModel } from "../../models/user.model";
import { ClaimModel } from "../../models/claim.model";
import { AuthModel } from "../../models/auth.model";
import { ResultModel } from "src/app/models/result.model";
import { RegisterResponseModel } from "../../models/register-response.model";
import { ChangePasswordResponseModel } from "src/app/models/change-password/change-password-response.model";
import { ChangePasswordModel } from "src/app/models/change-password/change-password.model";
import { ResultResendActivationCodeModel } from "src/app/models/result-resend-activation-code.model";
import { ForgotChangePasswordModel } from "src/app/models/forgot-change-password/change-password.model";
import { ForgotChangePasswordResponseModel } from "src/app/models/forgot-change-password/forgot-change-password-response.model";

// const API_USERS_URL = `${environment.apiUrl}/auth`;
const ACCOUNT_USERS_URL = `${environment.idsUrl}/account`;

let urls = Array('http://localhost:44303');
const clientId = `${environment.clientId}`;

export const authCodeFlowConfig: AuthConfig = {
    // Url of the Identity Provider
    issuer: `${environment.idsUrl}`,
    requireHttps: false,//true
    //allowedUrls:urls,
    // URL of the SPA to redirect the user to after login
    redirectUri: window.location.origin + '/index.html',
    // URL of the SPA to redirect the user after silent refresh
    silentRefreshRedirectUri: window.location.origin + '/silent-refresh.html',
    // The SPA's id. The SPA is registerd with this id at the auth-server
    // clientId: 'server.code',
    clientId: clientId,//'patg_public_mobile',
    // Just needed if your auth server demands a secret. In general, this
    // is a sign that the auth server is not configured with SPAs in mind
    // and it might not enforce further best practices vital for security
    // such applications.
    dummyClientSecret: 'patg_public_mobilev1',
    responseType: 'code',
    // set the scope for the permissions the client should request
    // The first four are defined by OIDC.
    // Important: Request offline_access to get a refresh token
    // The api scope is a usecase specific one
    scope: 'openid profile email api.read', //offline_access api
    showDebugInformation: true,
    tokenEndpoint: environment.IDS_CONFIG_TOKENENDPOINT,
    userinfoEndpoint: environment.IDS_CONFIG_USERINFOENDPOINT,
    skipIssuerCheck: true,
    oidc: false,
};

authCodeFlowConfig.strictDiscoveryDocumentValidation = false;

interface LoginInputModel {
    username: string, password: string //, rememberLogin:boolean, returnUrl:string
}

@Injectable({
    providedIn: 'root',
})
export class AuthHTTPService {

  constructor(private http: HttpClient,
      private oauthService: OAuthService,

  ) {
     // console.log(authCodeFlowConfig);
     // console.log(authCodeFlowConfig.issuer);
      this.oauthService.configure(authCodeFlowConfig);
      this.oauthService.loadDiscoveryDocumentAndTryLogin();
  }

  login(username: string, password: string): Observable<any> {
      const promise = this.oauthService
          .fetchTokenUsingPasswordFlowAndLoadUserProfile(
              username,
              password
          ).then(response => {
              const info = (response as any).info;
              const userModel = this.getUserModel(info);
              return userModel;
          })

      const observable = from(promise);
      return observable;
  }

  /**
   * Register user with selfie image
   * @param formData
   * @returns
   */
  createNewUser(formData: FormData): Observable<RegisterResponseModel> {
    // return this.http.post<RegisterResponseModel>(`${ACCOUNT_USERS_URL}/RegisterNewUser`, formData);
    return this.http.post<RegisterResponseModel>(`${ACCOUNT_USERS_URL}/RegisterNewUser2`, formData);
  }

  /**
   * Change user password
   * @param changePasswordModel
   * @returns
   */
  changePassword(changePasswordModel: ChangePasswordModel): Observable<ChangePasswordResponseModel> {
    return this.http.post<ChangePasswordResponseModel>(`${ACCOUNT_USERS_URL}/ChangePassword`, changePasswordModel);
  }

  /**
   * Change user password
   * @param changePasswordModel
   * @returns
   */
  resetPassword(resetPasswordModel: ForgotChangePasswordModel): Observable<ForgotChangePasswordResponseModel> {
    return this.http.post<ForgotChangePasswordResponseModel>(`${ACCOUNT_USERS_URL}/ResetPasswordByOTP`, resetPasswordModel);
  }

  requestResendActivationCode(username: string, channel?: string): Observable<ResultResendActivationCodeModel> {
    const url = channel
      ? `${ACCOUNT_USERS_URL}/RequestResendActivationCode?username=${username}&channel=${channel}`
      : `${ACCOUNT_USERS_URL}/RequestResendActivationCode?username=${username}`;
    return this.http.post<ResultResendActivationCodeModel>(url, username);
  }

  requestsendActivationCode(username: string): Observable<ResultModel> {
    return this.http.post<ResultModel>(`${ACCOUNT_USERS_URL}/RequestSendActivationCode?username=${username}`, username);
  }

  verifyActivationCode(code: string, phoneNumber: string, username: string): Observable<ResultModel> {
    return this.http.post<ResultModel>(`${ACCOUNT_USERS_URL}/VerifyPhoneConfirmationCode?code=${code}&phoneNumber=${phoneNumber}&username=${username}`,
    {
      code:code,
      phoneNumber: phoneNumber,
      username: username
    });
  }

  verifyToken(code: string, phoneNumber: string, username: string): Observable<ResultModel> {
    return this.http.post<ResultModel>(`${ACCOUNT_USERS_URL}/VerifyToken?code=${code}&phoneNumber=${phoneNumber}&username=${username}`,
    {
      code:code,
      phoneNumber: phoneNumber,
      username: username
    });
  }

  // Your server should check email => If email exists send link to the user and return true | If email doesn't exist return false
  forgotPassword(email: string): Observable<ResultModel> {
    return this.http.post<ResultModel>(`${ACCOUNT_USERS_URL}/RequestForgotPassword?email=${email}`, email);
  }

  forgotPasswordByPhoneNumber(username: string, channel?: string): Observable<ResultModel> {
    const url = channel
      ? `${ACCOUNT_USERS_URL}/RequestForgotPasswordMobile?username=${username}&channel=${channel}`
      : `${ACCOUNT_USERS_URL}/RequestForgotPasswordMobile?username=${username}`;
    return this.http.post<ResultModel>(url, username);
  }

  getUserModel(loginResponseModel: any): UserModel {
      let claims: any = this.oauthService.getIdentityClaims();
      const accessToken = this.oauthService.getAccessToken();
      const refreshToken = this.oauthService.getRefreshToken();
      const userClaim = new ClaimModel();
      userClaim.userId = claims['sub'];
      userClaim.email = claims['email'];
      userClaim.preferred_username = claims['preferred_username'];
      userClaim.name = claims['name'];
      userClaim.role = claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      const userModel: UserModel = new UserModel();
      userModel.accessToken = accessToken;
      userModel.refreshToken = refreshToken;
      userModel.claim = userClaim;
      userModel.expiresIn = new Date();
      userModel.memberId = loginResponseModel['MemberId'] === '' ? 0 : +loginResponseModel['MemberId'];
      userModel.id = loginResponseModel['sub'];
      userModel.username = claims['preferred_username'];// loginResponseModel['userName'];
      userModel.nric = claims['preferred_username']; //loginResponseModel['userName'];
      userModel.name = loginResponseModel['given_name'];
      userModel.email = loginResponseModel['email'];
      userModel.roles = new Array(loginResponseModel['role']);
      userModel.language = 'ms';
      userModel.accountStatus = loginResponseModel['accountStatus'];
      userModel.phoneNumber = loginResponseModel['phone_number'];
      userModel.officeId = loginResponseModel['officeId'];
      return userModel;
  }

  getUserByToken(token: string): Observable<UserModel> {
    let claims: any = this.oauthService.getIdentityClaims();
    if(claims == null){
      let user: UserModel = {
        id: '',
        username: '',
        nric: '',
        password: '',
        accountStatus: '0',
        name: '',
        email: '',
        profileImage: './assets/images/profile-placeholder.png',
        roles: [],
        occupation: '',
        companyName: '',
        phoneNumber: '',
        language: '',
        setUser: function (_user: unknown): void {
          throw new Error('Function not implemented.');
        },
        accessToken: '',
        refreshToken: '',
        expiresIn: new Date(),
        claim: new ClaimModel,
        setAuth: function (auth: AuthModel): void {
          throw new Error('Function not implemented.');
        },
        memberId:0,
        city:'',
        officeId: 0
      };
      return of(user);
    }

    // return user info
    var today = new Date();
    today.setHours(today.getHours() + 1);
    let user: UserModel = {
      id: claims['sub'],
      email: claims['email'],
      username: claims['preferred_username'],
      name: claims['name'],
      nric: '',
      password: '',
      accountStatus: '0',
      profileImage:'./assets/images/profile-placeholder.png',
      roles: [],
      occupation: '',
      companyName: '',
      phoneNumber: '',
      language: '',
      setUser: function (_user: unknown): void {
        throw new Error('Function not implemented.');
      },
      accessToken: '',
      refreshToken: '',
      expiresIn: today,
      claim: new ClaimModel,
      setAuth: function (auth: AuthModel): void {
        throw new Error('Function not implemented.');
      },
      memberId:0,
      city:'',
      officeId: 0
    }

    return of(user);
  }

  requestRefreshToken(refreshToken: string){
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${refreshToken}`
      })
    };
    return this.http.post<any>(`${environment.idsUrl}/token/refresh`, {}, httpOptions);
  }

}
